import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Avatar, Tag, Button, Space, Modal, Form, Input, Select, Checkbox, Progress } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  LockOutlined,
  BankOutlined,
  IdcardOutlined,
  FileProtectOutlined,
  CopyOutlined,
  CheckOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { withCountryCode } from "../../../utils/phones";

const { Option } = Select;

const UserProfileHeader = ({ userData, onUpdateUser, onUpdateStatus, isUpdatingProfile, isUpdatingStatus, statesData = [], statesLoading }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const FIELD_LABELS = { phone: "Phone", email: "Email", password: "Password" };

  const handleCopy = (value, field) => {
    if (!value || value === "N/A") return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      toast.success(`${FIELD_LABELS[field] || field} copied!`, { autoClose: 1500 });
      setTimeout(() => setCopiedField(null), 1500);
    });
  };
  const [form] = Form.useForm();
  const [gstInclusion, setGstInclusion] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordValue, setPasswordValue] = useState("");
  

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return "#ff4d4f";
    if (passwordStrength < 70) return "#faad14";
    return "#52c41a";
  };

  const getPasswordStrengthStatus = () => {
    if (passwordStrength < 40) return "exception";
    if (passwordStrength < 70) return "normal";
    return "success";
  };

  const calcStrength = (pwd) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 6) strength += 25;
    if (pwd.length >= 10) strength += 25;
    if (/[A-Z]/.test(pwd)) strength += 15;
    if (/[a-z]/.test(pwd)) strength += 15;
    if (/[0-9]/.test(pwd)) strength += 10;
    if (/[^A-Za-z0-9]/.test(pwd)) strength += 10;
    return Math.min(strength, 100);
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPasswordValue(val);
    setPasswordStrength(calcStrength(val));
  };

  const handleEdit = () => {
    const gst = userData?.gst_inclusion === 1 || userData?.gst_inclusion === true;
    setGstInclusion(gst);
    setPasswordValue("");
    setPasswordStrength(0);
    form.setFieldsValue({
      name: userData?.name,
      email: userData?.email,
      mobile: userData?.mobile,
      billing_address: userData?.address ?? userData?.billing_address,
      state: userData?.state ? String(userData.state) : undefined,
      pincode: userData?.pincode,
      company_name: userData?.company_name,
      gst: userData?.gst,
      // password: "",
      // confirm_password: "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (values) => {
    const payload = {
      ...values,
      gst_inclusion: gstInclusion ? 1 : 0,
    };
    // Remove GST fields if not applicable
    if (!gstInclusion) {
      delete payload.company_name;
      delete payload.gst;
    }
    // Remove password fields if password is not being changed
    if (!payload.password) {
      delete payload.password;
      delete payload.confirm_password;
    }
    await onUpdateUser(payload);
    setIsEditModalOpen(false);
    form.resetFields();
    setPasswordValue("");
    setPasswordStrength(0);
  };

  const toggleStatus = async () => {
    const newStatus = userData?.is_active === 1 ? 0 : 1;
    await onUpdateStatus(newStatus);
  };

  return (
    <>
      <Card className="user-profile-header shadow-sm">
        <div className="row align-items-center">
          {/* Avatar & Basic Info */}
          <div className="col-lg-3 col-md-4 text-center mb-3 mb-md-0">
            <Avatar
              size={120}
              icon={<UserOutlined />}
              src={userData?.image_url || "https://docsbizkit.com/admin/img/avatar.svg"}
              className="mb-3"
            />
            <h4 className="mb-2 fw-bold">{userData?.name || "N/A"}</h4>
            <Tag
              icon={userData?.is_active === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              color={userData?.is_active === 1 ? "success" : "error"}
              className="mb-2"
            >
              {userData?.is_active === 1 ? "ACTIVE" : "INACTIVE"}
            </Tag>
            <div className="mt-2">
              <Link
                to={`/cms/manageUserData/user-360-view/${userData?.id}`}
                state={{ userName: userData?.name }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 16px",
                  borderRadius: 20,
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  textDecoration: "none",
                  boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
                }}
              >
                <i className="bx bx-globe" style={{ fontSize: 14 }} /> 360° View
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="col-lg-5 col-md-4 mb-3 mb-md-0">
            <div className="user-contact-info">
              <Space direction="vertical" size="middle" className="w-100">
                <div className="d-flex align-items-center">
                  <PhoneOutlined className="me-2 text-primary" />
                  <span className="text-muted">Phone:</span>
                  <strong className="ms-2">{withCountryCode(userData?.country_code, userData?.mobile) || "N/A"}</strong>
                  <span
                    onClick={() => handleCopy(userData?.mobile, "phone")}
                    title="Copy phone number"
                    style={{
                      marginLeft: 8, cursor: "pointer", display: "inline-flex", alignItems: "center",
                      color: copiedField === "phone" ? "#52c41a" : "#8c8c8c",
                      transition: "color 0.2s",
                    }}
                  >
                    {copiedField === "phone" ? <CheckOutlined style={{ fontSize: 13 }} /> : <CopyOutlined style={{ fontSize: 13 }} />}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <MailOutlined className="me-2 text-primary" />
                  <span className="text-muted">Email:</span>
                  <strong className="ms-2">{userData?.email || "N/A"}</strong>
                  <span
                    onClick={() => handleCopy(userData?.email, "email")}
                    title="Copy email"
                    style={{
                      marginLeft: 8, cursor: "pointer", display: "inline-flex", alignItems: "center",
                      color: copiedField === "email" ? "#52c41a" : "#8c8c8c",
                      transition: "color 0.2s",
                    }}
                  >
                    {copiedField === "email" ? <CheckOutlined style={{ fontSize: 13 }} /> : <CopyOutlined style={{ fontSize: 13 }} />}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <LockOutlined className="me-2 text-primary" />
                  <span className="text-muted">Password:</span>
                  <strong className="ms-2" style={{ letterSpacing: showPassword ? 0 : 2 }}>
                    {userData?.plain_password
                      ? showPassword ? userData.plain_password : "••••••••"
                      : "N/A"}
                  </strong>
                  <span
                    onClick={() => setShowPassword((v) => !v)}
                    title={showPassword ? "Hide password" : "Show password"}
                    style={{ marginLeft: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", color: "#8c8c8c" }}
                  >
                    {showPassword
                      ? <EyeInvisibleOutlined style={{ fontSize: 13 }} />
                      : <EyeOutlined style={{ fontSize: 13 }} />}
                  </span>
                  <span
                    onClick={() => handleCopy(userData?.plain_password, "password")}
                    title="Copy password"
                    style={{
                      marginLeft: 6, cursor: "pointer", display: "inline-flex", alignItems: "center",
                      color: copiedField === "password" ? "#52c41a" : "#8c8c8c",
                      transition: "color 0.2s",
                    }}
                  >
                    {copiedField === "password" ? <CheckOutlined style={{ fontSize: 13 }} /> : <CopyOutlined style={{ fontSize: 13 }} />}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <UserOutlined className="me-2 text-primary" />
                  <span className="text-muted">User Type:</span>
                  <strong className="ms-2">{userData?.user_type || "N/A"}</strong>
                </div>
              </Space>
            </div>
          </div>

          {/* Stats */}
          <div className="col-lg-2 col-md-2 mb-3 mb-md-0">
            <div className="user-stats">
              <div className="stat-item text-center mb-3">
                <h3 className="mb-0 text-primary">{userData?.attorney_numbers?.length || 0}</h3>
                <small className="text-muted">Total Attorney</small>
              </div>
              <div className="stat-item text-center">
                <h3 className="mb-0 text-success">{userData?.total_tm_count || 0}</h3>
                <small className="text-muted">Total TM Count</small>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="col-lg-2 col-md-2 text-center text-md-end">
            <Space direction="vertical" className="w-100">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEdit}
                block
                loading={isUpdatingProfile}
              >
                Edit Profile
              </Button>
              <Button
                type={userData?.is_active === 1 ? "default" : "primary"}
                danger={userData?.is_active === 1}
                onClick={toggleStatus}
                block
                loading={isUpdatingStatus}
              >
                {userData?.is_active === 1 ? "Deactivate" : "Activate"}
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit User Profile"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          form.resetFields();
          setPasswordValue("");
          setPasswordStrength(0);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdateUser}>
          {/* Basic Details */}
          <div className="section-divider">
            <span><UserOutlined /> Basic Details</span>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: "Please enter name" }, { min: 3, message: "At least 3 characters" }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Enter full name" size="large" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: "Please enter email" }, { type: "email", message: "Invalid email" }]}
              >
                <Input prefix={<MailOutlined />} placeholder="Enter email" size="large" />
              </Form.Item>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label="Mobile"
                name="mobile"
                rules={[
                  { required: true, message: "Please enter mobile" },
                  { pattern: /^[6-9]\d{9}$/, message: "Enter valid 10-digit mobile" },
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="10-digit mobile number" size="large" maxLength={10} />
              </Form.Item>
            </div>
          </div>

          {/* Location Details */}
          <div className="section-divider">
            <span><EnvironmentOutlined /> Location Details</span>
          </div>

          <Form.Item
            label="Address"
            name="billing_address"
            rules={[{ required: true, message: "Address is required" }]}
          >
            <Input.TextArea placeholder="Street address, apartment, suite, etc." rows={3} />
          </Form.Item>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label="State"
                name="state"
                rules={[{ required: true, message: "State is required" }]}
              >
                <Select
                  placeholder="Select state"
                  size="large"
                  showSearch
                  loading={statesLoading}
                  optionFilterProp="label"
                  options={statesData}
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Pincode"
                name="pincode"
                rules={[
                  { required: true, message: "Pincode is required" },
                  { pattern: /^\d{6}$/, message: "Enter valid 6-digit pincode" },
                ]}
              >
                <Input prefix={<EnvironmentOutlined />} placeholder="6-digit pincode" size="large" maxLength={6} />
              </Form.Item>
            </div>
          </div>

          {/* Security */}
          {/* <div className="section-divider">
            <span><LockOutlined /> Security</span>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Item
                label="Password (leave blank to keep current)"
                name="password"
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Enter new password"
                  size="large"
                  onChange={handlePasswordChange}
                  autoComplete="new-password"
                />
              </Form.Item>
              {passwordValue && (
                <Progress
                  percent={passwordStrength}
                  strokeColor={getPasswordStrengthColor()}
                  status={getPasswordStrengthStatus()}
                  size="small"
                  format={(p) => (p < 40 ? "Weak" : p < 70 ? "Medium" : "Strong")}
                  style={{ marginTop: -16, marginBottom: 16 }}
                />
              )}
            </div>
            <div className="col-md-6">
              <Form.Item
                label="Confirm Password"
                name="confirm_password"
                dependencies={["password"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const pwd = getFieldValue("password");
                      if (!pwd) return Promise.resolve();
                      if (!value) return Promise.reject(new Error("Please confirm your password"));
                      if (pwd !== value) return Promise.reject(new Error("Passwords do not match"));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Re-enter new password"
                  size="large"
                  autoComplete="new-password"
                />
              </Form.Item>
            </div>
          </div> */}

          {/* GST Details */}
          <div className="section-divider">
            <span><FileProtectOutlined /> GST Registration</span>
          </div>

          <div
            style={{
              border: `2px solid ${gstInclusion ? "#667eea" : "#d9d9d9"}`,
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              background: gstInclusion ? "#f0f4ff" : "white",
              transition: "all 0.3s",
            }}
          >
            <Checkbox
              checked={gstInclusion}
              onChange={(e) => setGstInclusion(e.target.checked)}
            >
              <span style={{ fontWeight: 600 }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Customer has GST Registration
              </span>
            </Checkbox>
          </div>

          {gstInclusion && (
            <div className="row">
              <div className="col-md-6">
                <Form.Item
                  label="Company Name"
                  name="company_name"
                  rules={[{ required: true, message: "Company name is required" }]}
                >
                  <Input prefix={<BankOutlined />} placeholder="Registered company name" size="large" />
                </Form.Item>
              </div>
             <div className="col-md-6">
                <Form.Item
                  label="GST Number"
                  name="gst"
                  rules={[
                    { required: true, message: "GST number is required" },
                    { len: 15, message: "GSTIN must be exactly 15 characters" },
                    {
                      pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/,
                      message: "Invalid GSTIN format. Expected: 27ABCDE1234F2Z5",
                    },
                  ]}
                  normalize={(value) => (value ? value.toUpperCase() : value)}
                >
                  <Input
                    prefix={<IdcardOutlined />}
                    placeholder="e.g. 27ABCDE1234F2Z5"
                    size="large"
                    maxLength={15}
                  />
                </Form.Item>
              </div>
            </div>
          )}

          <Form.Item className="mb-0" style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
            <Space className="w-100 justify-content-end">
              <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={isUpdatingProfile}
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}>
                Update Profile
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .user-profile-header {
          border-radius: 12px;
          margin-bottom: 24px;
        }
        .user-contact-info {
          padding: 0 20px;
        }
        .stat-item {
          padding: 12px;
          background: #f5f5f5;
          border-radius: 8px;
        }
        .stat-item h3 {
          font-weight: 700;
        }
        .section-divider {
          font-weight: 700;
          font-size: 15px;
          color: #667eea;
          border-bottom: 2px solid #e8ecff;
          padding-bottom: 8px;
          margin-bottom: 16px;
          margin-top: 8px;
        }
      `}</style>
    </>
  );
};

export default UserProfileHeader;